/**
 * metaPixel.js — Browser-side Meta Pixel + Conversions API dual-fire.
 *
 * Pattern:
 *   • Each tracked event gets a fresh UUID `event_id`.
 *   • The same event_id is passed to BOTH `fbq('track', ...)` AND the
 *     backend `POST /api/meta/track` endpoint. Meta dedupes the two halves
 *     so reports aren't double-counted.
 *   • PII (email, phone, name, ...) is sent as PLAINTEXT to OUR backend
 *     over HTTPS; the backend hashes (SHA-256) before forwarding to Meta.
 *     We never put plaintext PII on the wire to Meta from the browser.
 *
 * Public exports:
 *   • initMetaPixel()            — no-op; the inline snippet in index.html
 *                                  has already initialised window.fbq.
 *   • trackEvent(name, custom, user)
 *   • trackPageView()
 *   • trackViewContent(product, variant?)
 *   • trackSearch(query, results?)
 *   • trackAddToCart(product, variant, qty)
 *   • trackInitiateCheckout(items, totals, address?)
 *   • trackAddPaymentInfo(items, totals, method)
 *   • trackPurchase(order)
 *   • trackCompleteRegistration(method?)
 */
import api from "./api";

// iter52 — Pixel id is also hard-coded as a defensive fallback. The
// canonical source is REACT_APP_META_PIXEL_ID in /app/frontend/.env, but
// any future env-var typo (e.g. concatenated lines, missing key) would
// otherwise silently disable the entire CAPI dual-fire because trackEvent
// short-circuits when PIXEL_ID is falsy. Keeping a literal fallback means
// the worst case is "we still send events to the right Pixel".
const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || "947337991672653";

// Browser may not have crypto.randomUUID in older Safari — fallback.
function uuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function getCookie(name) {
  if (typeof document === "undefined") return "";
  const m = document.cookie.match(new RegExp("(^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[2]) : "";
}

function buildUserData(extra) {
  const fbp = getCookie("_fbp");
  const fbc = getCookie("_fbc");
  const ud = { ...(extra || {}) };
  if (fbp) ud.fbp = fbp;
  if (fbc) ud.fbc = fbc;
  return ud;
}

/**
 * Fire BOTH the browser Pixel and the server CAPI mirror.
 *  • customData — Meta's "custom_data" object (value, currency, contents, ...)
 *  • userData   — optional PII the backend will hash (em, ph, fn, ln, ...)
 *  • eventId    — pass to reuse a pre-generated id (e.g. server Purchase)
 */
export function trackEvent(eventName, customData = {}, userData = {}, eventId = null) {
  if (!PIXEL_ID) return null;
  const id = eventId || uuid();

  // 1) Browser Pixel
  try {
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("track", eventName, customData, { eventID: id });
    }
  } catch (e) {
    // Pixel never throws but be paranoid — analytics must NEVER break UX.
  }

  // 2) Server CAPI — fire-and-forget; never await on the hot path.
  try {
    const merged = buildUserData(userData);
    api
      .post("/meta/track", {
        event_name: eventName,
        event_id: id,
        event_source_url: typeof window !== "undefined" ? window.location.href : "",
        custom_data: customData,
        user_data: merged,
      })
      .catch(() => {});
  } catch (e) {
    /* swallow */
  }

  return id;
}

export function initMetaPixel() {
  // The base `fbq('init', ...)` call lives in index.html so the Pixel
  // is ready before the React bundle mounts. This function exists for
  // symmetry and any future runtime config.
}

// ── Standard e-commerce events ──────────────────────────────────────────
export function trackPageView() {
  trackEvent("PageView");
}

export function trackViewContent(product, variant) {
  if (!product) return;
  const price = variant?.price ?? product.price ?? 0;
  trackEvent("ViewContent", {
    content_ids: [String(product.product_id || product.id || product.name)],
    content_name: product.name || "",
    content_type: "product",
    content_category: product.category || "",
    currency: "INR",
    value: Number(price) || 0,
  });
}

export function trackSearch(query, results) {
  if (!query || !query.trim()) return;
  trackEvent("Search", {
    search_string: String(query).slice(0, 200),
    content_type: "product",
    ...(typeof results === "number" ? { num_items: results } : {}),
  });
}

export function trackAddToCart(product, variant, qty = 1) {
  if (!product) return;
  const price = variant?.price ?? product.price ?? 0;
  trackEvent("AddToCart", {
    content_ids: [String(product.product_id || product.id || product.name)],
    content_name: product.name || "",
    content_type: "product",
    content_category: product.category || "",
    contents: [
      {
        id: String(product.product_id || product.id || product.name),
        quantity: Number(qty) || 1,
        item_price: Number(price) || 0,
      },
    ],
    currency: "INR",
    value: Number(price) * Number(qty || 1),
  });
}

function cartToContents(items) {
  return (items || []).map((it) => ({
    id: String(it.product_id || it.id || it.name),
    quantity: Number(it.quantity) || 1,
    item_price: Number(it.price) || 0,
  }));
}

export function trackInitiateCheckout(items, totals, address) {
  trackEvent(
    "InitiateCheckout",
    {
      content_type: "product",
      contents: cartToContents(items),
      content_ids: cartToContents(items).map((c) => c.id),
      num_items: cartToContents(items).reduce((a, c) => a + c.quantity, 0),
      currency: "INR",
      value: Number(totals?.total ?? totals?.subtotal ?? 0),
    },
    address
      ? {
          em: address.email || undefined,
          ph: address.phone || undefined,
          fn: address.full_name ? address.full_name.split(" ")[0] : undefined,
          ln: address.full_name && address.full_name.split(" ").length > 1
            ? address.full_name.split(" ").slice(1).join(" ")
            : undefined,
          ct: address.city || undefined,
          st: address.state || undefined,
          zp: address.pincode || undefined,
          country: address.country || "IN",
        }
      : {}
  );
}

export function trackAddPaymentInfo(items, totals, method) {
  trackEvent("AddPaymentInfo", {
    content_type: "product",
    contents: cartToContents(items),
    content_ids: cartToContents(items).map((c) => c.id),
    currency: "INR",
    value: Number(totals?.total ?? totals?.subtotal ?? 0),
    payment_method: method || "razorpay",
  });
}

/**
 * Purchase — should be fired ONCE per successful order on the confirmation
 * page. The server also fires Purchase with the same event_id (stored as
 * order.meta_purchase_event_id) so Meta dedupes browser + server halves.
 */
export function trackPurchase(order) {
  if (!order) return;
  const eventId = order.meta_purchase_event_id || `purchase_${order.order_id}`;
  const addr = order.shipping_address || order.address || {};
  trackEvent(
    "Purchase",
    {
      content_type: "product",
      contents: cartToContents(order.items),
      content_ids: cartToContents(order.items).map((c) => c.id),
      num_items: cartToContents(order.items).reduce((a, c) => a + c.quantity, 0) || 1,
      currency: "INR",
      value: Number(order.total) || 0,
      order_id: order.order_id || "",
    },
    {
      em: order.customer_email || undefined,
      ph: addr.phone || undefined,
      fn: addr.full_name ? addr.full_name.split(" ")[0] : undefined,
      ln: addr.full_name && addr.full_name.split(" ").length > 1
        ? addr.full_name.split(" ").slice(1).join(" ")
        : undefined,
      ct: addr.city || undefined,
      st: addr.state || undefined,
      zp: addr.pincode || undefined,
      country: addr.country || "IN",
      external_id: order.user_id || undefined,
    },
    eventId
  );
}

export function trackCompleteRegistration(method) {
  trackEvent("CompleteRegistration", {
    content_name: "account_signup",
    status: "completed",
    ...(method ? { registration_method: method } : {}),
  });
}
